import React, { useEffect, useState } from "react";
import { Camera, Download, QrCode, Send, Share2, Trash2, Upload, X } from "lucide-react";
import { useConfirm } from "../../components/ConfirmProvider";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

export default function PhotoGalleryDialog({ item, onClose, onChanged }) {
  const confirm = useConfirm();
  const [photoIds, setPhotoIds] = useState(item.photo_ids || []);
  const [uploading, setUploading] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [persons, setPersons] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [shareMsg, setShareMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);

  useEffect(() => {
    api.get("/persons").then((r) => setPersons(r.data || [])).catch(() => setPersons([]));
  }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      let updatedIds = photoIds;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/inventory/${item.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        updatedIds = data.photo_ids || updatedIds;
      }
      setPhotoIds(updatedIds);
      toast.success(`${files.length} poz${files.length === 1 ? "ă" : "e"} încărcat${files.length === 1 ? "ă" : "e"}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (pid) => {
    if (!(await confirm({ body: "Ștergi această poză?", danger: true }))) return;
    try {
      const { data } = await api.delete(`/inventory/${item.id}/photos/${pid}`);
      setPhotoIds(data.photo_ids || []);
      onChanged();
      toast.success("Poză ștearsă");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const handleDownload = async (pid) => {
    try {
      const res = await api.get(`/files/${pid}?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.name || "echipament"}-${pid.slice(0, 6)}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Eroare la descărcare");
    }
  };

  const handleWhatsApp = (pid) => {
    const url = fileUrl(pid);
    const textParts = [`📦 ${item.name}`];
    if (item.note) textParts.push(item.note);
    if (item.location) textParts.push(`Locație: ${item.location}`);
    textParts.push(`Poză: ${url}`);
    const waUrl = `https://wa.me/?text=${encodeURIComponent(textParts.join("\n"))}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const togglePerson = (pid) => {
    setSelectedPersons((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  };

  const sendToEmployees = async () => {
    if (!selectedPersons.length) { toast.warning("Selectează cel puțin un angajat"); return; }
    setSending(true);
    try {
      await api.post(`/inventory/${item.id}/share`, {
        person_ids: selectedPersons,
        message: shareMsg || null,
        photo_id: photoIds[0] || null,
      });
      toast.success(`Trimis către ${selectedPersons.length} angajat${selectedPersons.length === 1 ? "" : "i"}`);
      setSelectedPersons([]);
      setShareMsg("");
      setShareOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setSending(false);
    }
  };

  const openQr = async () => {
    try {
      const res = await api.get(`/inventory/${item.id}/qr`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setQrUrl(url);
      setQrOpen(true);
    } catch {
      toast.error("Eroare la generarea QR");
    }
  };

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `${(item.name || "echipament").replace(/\s+/g, "-")}-${item.id.slice(0, 8)}-QR.png`;
    a.click();
  };

  const shareQrWhatsApp = () => {
    // WhatsApp can't embed arbitrary images from URL directly; send equipment link instead
    const target = `${window.location.origin}/inventar?open=${item.id}`;
    const textParts = [`📦 ${item.name}`];
    if (item.note) textParts.push(item.note);
    textParts.push(`🔗 ${target}`);
    const waUrl = `https://wa.me/?text=${encodeURIComponent(textParts.join("\n"))}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera size={18} className="text-[var(--primary)]"/>
              {item.name} — Galerie foto
            </DialogTitle>
          </DialogHeader>

          <div className="text-xs text-zinc-600 -mt-2 mb-2">
            {item.note && <><strong>Detalii:</strong> {item.note}{item.location ? ` · 📍 ${item.location}` : ""}</>}
            {!item.note && item.location && <>📍 {item.location}</>}
          </div>

          {/* Upload button */}
          <div className="flex gap-2 mb-3 flex-wrap">
            <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--primary)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
              <Upload size={14}/>
              {uploading ? "Încarc..." : "Încarcă poze"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} data-testid="upload-photos-input"/>
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareOpen(true)}
              disabled={!photoIds.length}
              data-testid="open-share-btn"
            >
              <Send size={14} className="mr-1.5"/> Trimite la angajat
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={openQr}
              data-testid="open-qr-btn"
            >
              <QrCode size={14} className="mr-1.5"/> Cod QR
            </Button>
          </div>

          {/* Gallery */}
          {photoIds.length === 0 ? (
            <div className="text-center py-10 text-zinc-400 text-sm border-2 border-dashed border-zinc-200 rounded-md">
              <Camera size={32} className="mx-auto mb-2 text-zinc-300"/>
              Nicio poză încărcată încă. Apasă <strong>„Încarcă poze"</strong> pentru a adăuga.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto">
              {photoIds.map((pid, idx) => (
                <div key={pid} className="relative group border border-zinc-200 rounded-md overflow-hidden bg-zinc-50">
                  <img
                    src={fileUrl(pid)}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-36 object-cover cursor-pointer hover:opacity-90"
                    onClick={() => setPreviewIdx(idx)}
                    data-testid={`photo-thumb-${idx}`}
                  />
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleDownload(pid)}
                      className="p-1 rounded bg-white/90 hover:bg-white text-zinc-700 shadow-sm"
                      title="Descarcă"
                    ><Download size={12}/></button>
                    <button
                      onClick={() => handleWhatsApp(pid)}
                      className="p-1 rounded bg-green-500/90 hover:bg-green-500 text-white shadow-sm"
                      title="WhatsApp"
                    ><Share2 size={12}/></button>
                    <button
                      onClick={() => handleDelete(pid)}
                      className="p-1 rounded bg-red-500/90 hover:bg-red-500 text-white shadow-sm"
                      title="Șterge"
                    ><Trash2 size={12}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fullscreen preview */}
      {previewIdx !== null && photoIds[previewIdx] && (
        <Dialog open onOpenChange={() => setPreviewIdx(null)}>
          <DialogContent className="max-w-5xl p-0 bg-black/95 border-0">
            <button
              onClick={() => setPreviewIdx(null)}
              className="absolute top-4 right-4 z-10 text-white bg-black/50 hover:bg-black/70 rounded-full p-2"
              aria-label="Închide"
            ><X size={20}/></button>
            <img
              src={fileUrl(photoIds[previewIdx])}
              alt="Preview"
              className="w-full max-h-[85vh] object-contain"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 rounded-full px-2 py-1.5">
              {previewIdx > 0 && (
                <button onClick={() => setPreviewIdx(previewIdx - 1)} className="text-white px-3 py-1 hover:bg-white/10 rounded-full text-sm">← Anterior</button>
              )}
              <button onClick={() => handleDownload(photoIds[previewIdx])} className="text-white px-3 py-1 hover:bg-white/10 rounded-full text-sm flex items-center gap-1"><Download size={14}/> Descarcă</button>
              <button onClick={() => handleWhatsApp(photoIds[previewIdx])} className="text-green-400 px-3 py-1 hover:bg-white/10 rounded-full text-sm flex items-center gap-1"><Share2 size={14}/> WhatsApp</button>
              {previewIdx < photoIds.length - 1 && (
                <button onClick={() => setPreviewIdx(previewIdx + 1)} className="text-white px-3 py-1 hover:bg-white/10 rounded-full text-sm">Următor →</button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Share to employees */}
      {shareOpen && (
        <Dialog open onOpenChange={(v) => !v && setShareOpen(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Send size={16}/> Trimite angajaților</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="small-label">Mesaj (opțional)</Label>
                <Textarea
                  rows={2}
                  value={shareMsg}
                  onChange={(e) => setShareMsg(e.target.value)}
                  placeholder="Ex: Verifică acest echipament înainte de luni."
                />
              </div>
              <div>
                <Label className="small-label">Selectează angajații ({selectedPersons.length})</Label>
                <div className="mt-2 max-h-60 overflow-y-auto border border-zinc-200 rounded-md divide-y">
                  {persons.length === 0 ? (
                    <div className="p-3 text-sm text-zinc-400">Nu există angajați.</div>
                  ) : persons.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 p-2.5 hover:bg-zinc-50 cursor-pointer">
                      <Checkbox
                        checked={selectedPersons.includes(p.id)}
                        onCheckedChange={() => togglePerson(p.id)}
                      />
                      <div className="flex-1 text-sm">
                        {p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim() || p.nume || "—"}
                        {p.functie && <span className="text-[11px] text-zinc-500 ml-2">· {p.functie}</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareOpen(false)}>Anulează</Button>
              <Button
                onClick={sendToEmployees}
                disabled={sending || !selectedPersons.length}
                className="bg-[var(--primary)]"
                data-testid="submit-share-btn"
              >
                <Send size={14} className="mr-1"/>{sending ? "Trimit…" : "Trimite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* QR code dialog */}
      {qrOpen && qrUrl && (
        <Dialog open onOpenChange={(v) => {
          if (!v) { setQrOpen(false); setTimeout(() => { if (qrUrl) URL.revokeObjectURL(qrUrl); setQrUrl(null); }, 300); }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode size={18} className="text-[var(--primary)]"/> Cod QR — {item.name}
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs text-zinc-600 -mt-2 mb-2">
              Scanează codul cu telefonul pentru a deschide direct fișa echipamentului (poze, istoric, detalii).
            </div>
            <div className="bg-white p-6 rounded-lg border-2 border-zinc-100 flex justify-center">
              <img src={qrUrl} alt="QR Code" className="w-64 h-64" data-testid="qr-image"/>
            </div>
            {item.note && <div className="text-center text-xs text-zinc-500 mt-2 font-mono">{item.note}</div>}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={downloadQr} data-testid="qr-download-btn">
                <Download size={14} className="mr-1.5"/> Descarcă PNG
              </Button>
              <Button onClick={shareQrWhatsApp} className="bg-green-600 hover:bg-green-700">
                <Share2 size={14} className="mr-1.5"/> Trimite link pe WhatsApp
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
